import type { FC } from 'react';
import { Paper, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import type { Theme } from '@mui/material';
import { useTranslation } from 'react-i18next';

import type { RevenueEntry } from '../types';

const useStyles = makeStyles((theme: Theme) => ({
  card: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    height: '100%',
    padding: theme.spacing(2.5),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1.5, 2),
    },
  },
  label: {
    display: 'block',
    marginBottom: theme.spacing(0.5),
    color: '#000000',
    fontWeight: 600,
    overflowWrap: 'anywhere' as const,
  },
  value: {
    marginTop: 'auto',
    marginBottom: theme.spacing(0.5),
    color: theme.palette.primary.main,
    fontFamily: 'monospace',
    fontWeight: 700,
    textAlign: 'right' as const,
    overflowWrap: 'anywhere' as const,
    wordBreak: 'break-word' as const,
    [theme.breakpoints.down('sm')]: {
      marginBottom: 0,
    },
  },
  valueUnavailable: {
    color: '#000000',
  },
  framing: {
    display: 'block',
    marginTop: theme.spacing(0.5),
    color: '#000000',
    overflowWrap: 'anywhere' as const,
  },
  note: {
    display: 'block',
    marginTop: theme.spacing(1),
    color: '#000000',
    overflowWrap: 'anywhere' as const,
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(0.5),
    },
  },
  noteError: {
    color: '#000000',
    fontWeight: 500,
  },
}));

type FootnoteColor = 'error' | 'textSecondary';

type KPIMetricCardProps = {
  readonly entry: RevenueEntry;
  readonly labelOverride?: string;
  readonly footnote?: string;
  readonly footnoteColor?: FootnoteColor;
};

const KPIMetricCard: FC<KPIMetricCardProps> = ({
  entry,
  labelOverride,
  footnote,
  footnoteColor = 'textSecondary',
}) => {
  const classes = useStyles();
  const { t } = useTranslation('revenue');
  const label = labelOverride ?? entry.label;
  const isZeroBasis = entry.available && !entry.hasBasis && entry.basisNote;

  return (
    <Paper elevation={1} className={classes.card}>
      <Typography variant="subtitle2" className={classes.label}>
        {label}
      </Typography>
      {entry.framing && (
        <Typography variant="caption" className={classes.framing}>
          {entry.framing}
        </Typography>
      )}
      {!entry.available && (
        <Typography variant="caption" className={classes.note}>
          {t('kpi.unavailable', 'Not available for this order')}
        </Typography>
      )}
      {isZeroBasis && (
        <Typography variant="caption" className={classes.note}>
          {t('kpi.basisNote', entry.basisNote)}
        </Typography>
      )}
      {footnote && (
        <Typography
          variant="caption"
          className={`${classes.note} ${footnoteColor === 'error' ? classes.noteError : ''}`}
        >
          {footnote}
        </Typography>
      )}
      <Typography
        variant="h5"
        className={`${classes.value} ${!entry.available ? classes.valueUnavailable : ''}`}
      >
        {entry.display}
      </Typography>
    </Paper>
  );
};

export { KPIMetricCard };
export default KPIMetricCard;
